/**
 * Canonical fundo (single-ticker) route contract.
 *
 * Resource routes:
 *   /ativo/{ticker}/resumo            -> default tab
 *   /ativo/{ticker}/simulacao
 *   /ativo/{ticker}/cotacao
 *   /ativo/{ticker}/liquidez
 *   /ativo/{ticker}/dados-cvm
 *   /ativo/{ticker}/dividendos
 *   /ativo/{ticker}/patrimonio
 *   /ativo/{ticker}/caixa             -> caixa overview
 *   /ativo/{ticker}/caixa/visao-geral
 *   /ativo/{ticker}/caixa/detalhe-caixa -> cash breakdown detail
 *   /ativo/{ticker}/caixa/recebiveis
 *   /ativo/{ticker}/caixa/titulos-fundos
 *   /ativo/{ticker}/caixa/a-pagar
 *   /ativo/{ticker}/carteira          -> portfolio tab (Visão geral)
 *   /ativo/{ticker}/carteira/visao-geral
 *   /ativo/{ticker}/carteira/imoveis-composicao
 *   /ativo/{ticker}/carteira/imoveis-risco
 *   /ativo/{ticker}/carteira/imoveis-contratos
 *   /ativo/{ticker}/carteira/fundos-investidos
 *   /ativo/{ticker}/carteira/ativos-financeiros
 *   /ativo/{ticker}/carteira/acoes-participacoes
 *   /ativo/{ticker}/relatorios
 *
 * `/ativo/{ticker}` redirects to `/ativo/{ticker}/resumo`.
 *
 * The "carteira" subview slug is independent from the "caixa" subview slug
 * so a URL like `/ativo/{ticker}/carteira/recebiveis` is treated as a
 * carteira subview (and falls back to `visao-geral`) rather than colliding
 * with the caixa subview namespace.
 *
 * Query params remain reserved for filters (indicators, date ranges,
 * amortisation toggles, report presets, etc.).
 */

export const ATIVO_PRIMARY_TABS = [
  'resumo',
  'simulacao',
  'cotacao',
  'liquidez',
  'dados-cvm',
  'dividendos',
  'patrimonio',
  'caixa',
  'carteira',
  'relatorios',
] as const;

export type AtivoPrimaryTab = (typeof ATIVO_PRIMARY_TABS)[number];

export const ATIVO_CAIXA_SUBVIEWS = [
  'visao-geral',
  'detalhe-caixa',
  'recebiveis',
  'titulos-fundos',
  'a-pagar',
] as const;

export type AtivoCaixaSubview = (typeof ATIVO_CAIXA_SUBVIEWS)[number];

/**
 * Carteira (portfolio) subviews. Stable Portuguese slugs so the URL is
 * shareable. Order matters — `visao-geral` is the default and first entry
 * for navigation.
 */
export const ATIVO_PORTFOLIO_SUBVIEWS = [
  'visao-geral',
  'imoveis-composicao',
  'imoveis-risco',
  'imoveis-contratos',
  'fundos-investidos',
  'ativos-financeiros',
  'acoes-participacoes',
] as const;

export type AtivoPortfolioSubview = (typeof ATIVO_PORTFOLIO_SUBVIEWS)[number];

export const ATIVO_PORTFOLIO_SUBVIEW_LABELS: Record<AtivoPortfolioSubview, string> = {
  'visao-geral': 'Visão geral',
  'imoveis-composicao': 'Imóveis/Composição',
  'imoveis-risco': 'Imóveis/Risco',
  'imoveis-contratos': 'Imóveis/Contratos',
  'fundos-investidos': 'Fundos investidos',
  'ativos-financeiros': 'Ativos financeiros',
  'acoes-participacoes': 'Ações/Participações',
};

export const ATIVO_INDEX_PATH = '/ativo' as const;

const ATIVO_SEGMENT_TO_LABEL: Record<AtivoPrimaryTab, string> = {
  resumo: 'Resumo',
  simulacao: 'Simulação',
  cotacao: 'Cotação',
  liquidez: 'Liquidez',
  'dados-cvm': 'Dados CVM',
  dividendos: 'Dividendos',
  patrimonio: 'Patrimônio',
  caixa: 'Caixa',
  carteira: 'Carteira',
  relatorios: 'Relatórios',
};

const isAtivoPrimaryTab = (value: string): value is AtivoPrimaryTab =>
  (ATIVO_PRIMARY_TABS as readonly string[]).includes(value);

const isAtivoCaixaSubview = (value: string): value is AtivoCaixaSubview =>
  (ATIVO_CAIXA_SUBVIEWS as readonly string[]).includes(value);

const isAtivoPortfolioSubview = (value: string): value is AtivoPortfolioSubview =>
  (ATIVO_PORTFOLIO_SUBVIEWS as readonly string[]).includes(value);

export const labelForAtivoTab = (tab: AtivoPrimaryTab) => ATIVO_SEGMENT_TO_LABEL[tab];

export const labelForAtivoPortfolioSubview = (subview: AtivoPortfolioSubview) =>
  ATIVO_PORTFOLIO_SUBVIEW_LABELS[subview];

/**
 * Resolves a `/ativo/{ticker}/{aba?}/{subview?}` path to the active tab
 * and (for the caixa or carteira tab) the current sub-view. `availability`
 * is the capability map reported by the ticker/tab resolver. The
 * `isValid` flag distinguishes "tab exists but the ticker doesn't" from
 * "tab is unknown and the page should redirect to resumo".
 */
export type AtivoAvailability = {
  showLiquidityTab: boolean;
  showCashTab: boolean;
  showPortfolioTab: boolean;
};

export const parseAtivoResourcePath = ({
  segments,
  availability,
}: {
  segments: ReadonlyArray<string>;
  availability: AtivoAvailability;
}) => {
  const [tickerSegment, tabSegment, subviewSegment] = segments;

  if (!tickerSegment) {
    return {
      ticker: null,
      tab: 'resumo' as AtivoPrimaryTab,
      caixaSubview: 'visao-geral' as AtivoCaixaSubview,
      portfolioSubview: 'visao-geral' as AtivoPortfolioSubview,
      isValid: false,
    };
  }

  let tab: AtivoPrimaryTab = 'resumo';
  let caixaSubview: AtivoCaixaSubview = 'visao-geral';
  let portfolioSubview: AtivoPortfolioSubview = 'visao-geral';

  if (tabSegment && isAtivoPrimaryTab(tabSegment)) {
    tab = tabSegment;
  } else if (tabSegment) {
    // Unknown primary tab — fall back to resumo.
    tab = 'resumo';
  }

  if (tab === 'liquidez' && !availability.showLiquidityTab) {
    tab = 'resumo';
  }
  if (tab === 'caixa' && !availability.showCashTab) {
    tab = 'resumo';
  }
  if (tab === 'carteira' && !availability.showPortfolioTab) {
    tab = 'resumo';
  }

  if (tab === 'caixa' && subviewSegment) {
    caixaSubview = isAtivoCaixaSubview(subviewSegment) ? subviewSegment : 'visao-geral';
  }

  if (tab === 'carteira' && subviewSegment) {
    portfolioSubview = isAtivoPortfolioSubview(subviewSegment) ? subviewSegment : 'visao-geral';
  }

  return {
    ticker: tickerSegment,
    tab,
    caixaSubview,
    portfolioSubview,
    isValid: true,
  };
};

export type AtivoPath = {
  ticker: string;
  tab: AtivoPrimaryTab;
  caixaSubview?: AtivoCaixaSubview;
  portfolioSubview?: AtivoPortfolioSubview;
};

const buildPath = ({ ticker, tab, caixaSubview, portfolioSubview }: AtivoPath): string => {
  const base = `/ativo/${encodeURIComponent(ticker)}/${tab}`;
  if (tab === 'caixa' && caixaSubview && caixaSubview !== 'visao-geral') {
    return `${base}/${caixaSubview}`;
  }
  if (tab === 'carteira' && portfolioSubview && portfolioSubview !== 'visao-geral') {
    return `${base}/${portfolioSubview}`;
  }
  return base;
};

export const ativoPath = {
  tab: (ticker: string, tab: AtivoPrimaryTab) => buildPath({ ticker, tab }),
  index: () => ATIVO_INDEX_PATH,
  /**
   * Cross-link helper: preserves the caixa sub-view only when the target
   * tab is `caixa`, otherwise the sub-view would leak into other pages.
   */
  withActiveCaixaSubview: ({ ticker, tab, caixaSubview }: AtivoPath) => buildPath({ ticker, tab, caixaSubview }),
  /**
   * Cross-link helper: preserves the carteira sub-view only when the
   * target tab is `carteira`, otherwise the sub-view would leak into
   * other pages.
   */
  withActivePortfolioSubview: ({ ticker, tab, portfolioSubview }: AtivoPath) =>
    buildPath({ ticker, tab, portfolioSubview }),
};

/** Builds a canonical instrument route while preserving its optional subview. */
export const canonicalAtivoResourcePath = ({
  ticker,
  tab,
  subview,
}: {
  ticker: string;
  tab: AtivoPrimaryTab;
  subview?: string;
}) => {
  const base = ativoPath.tab(ticker, tab);
  if (tab === 'caixa' && subview && isAtivoCaixaSubview(subview)) {
    return ativoPath.withActiveCaixaSubview({ ticker, tab, caixaSubview: subview });
  }
  if (tab === 'carteira' && subview && isAtivoPortfolioSubview(subview)) {
    return ativoPath.withActivePortfolioSubview({ ticker, tab, portfolioSubview: subview });
  }
  return base;
};
